/**
 * DealsListTable — Tabela padrão da listagem de Negócios
 *
 * Extraída (sem alteração visual) de `src/pages/crm/CrmDeals.tsx` para poder
 * ser reaproveitada tanto pela listagem normal quanto pela nova visão
 * "Agrupar por" (`DealGroupedView`), exatamente como o módulo de Tarefas faz
 * com `TaskListTable` + `TaskGroupedView`.
 *
 * Todo o estado da tela (ordenação, seleção em massa, permissões por fase e
 * handlers de abrir/editar/ganhar/perder/reabrir) chega por um Context provido
 * pela página — os grupos não duplicam nenhuma regra.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial (extração cirúrgica da tabela
 *          embutida em CrmDeals.tsx + DealsListTableContext)
 */
import React, { createContext, useContext } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Filter,
  Eye,
  Pencil,
  Trophy,
  XCircle,
  RotateCcw,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatDealCode } from '@/lib/formatters';
import type { PaginatedDeal, DealSortField, SortDirection } from '@/hooks/useCrmDealsPaginated';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { DealStatusBadge } from '@/components/crm/deals/DealStatusBadge';
import { DealAgeIndicator } from '@/components/crm/deals/DealAgeIndicator';
import { DealPriorityIndicator } from '@/components/crm/deals/DealPriorityIndicator';
import { DealOriginBadge } from '@/components/crm/deals/components/DealOriginBadge';
import { LeadSourceBadge } from '@/components/crm/deals/components/LeadSourceBadge';
import { DealTagBadge } from '@/components/crm/deals/tags/DealTagBadge';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Componente auxiliar para exibir progresso da fase no pipeline
 * Busca total de fases e calcula progresso baseado na posição
 */
export function PhaseProgressCell({
  deal,
  pipelineId,
}: {
  deal: PaginatedDeal;
  pipelineId: string;
}) {
  const { phases } = usePipelinePhaseStats(pipelineId);
  const totalPhases = phases.length || 1;

  return (
    <DealPhaseProgress
      phaseName={deal.phase_name || ''}
      phaseColor={deal.phase_color || null}
      phaseSortOrder={deal.phase_sort_order || 1}
      totalPhases={totalPhases}
    />
  );
}

/** Permissões calculadas por linha (mesma matriz do Kanban/Modal). */
export interface DealRowPerms {
  canEditRow: boolean;
  canChangePhaseRow: boolean;
  canReopenRow: boolean;
}

/** Tudo o que a tabela precisa da página que a hospeda. */
export interface DealsListTableContextValue {
  sortField: DealSortField;
  sortDirection: SortDirection;
  onSort: (field: DealSortField) => void;
  bulkEditMode: boolean;
  selectedDealIds: Set<string>;
  onToggleDealSelection: (dealId: string) => void;
  /** Marca/desmarca todos os negócios da tabela recebida. */
  onToggleSelectAll: (deals: PaginatedDeal[]) => void;
  onViewDeal: (deal: PaginatedDeal) => void;
  onEditDeal: (deal: PaginatedDeal) => void;
  computeRowPerms: (deal: PaginatedDeal) => DealRowPerms;
  onMarkAsWon: (dealId: string) => void;
  onMarkAsLost: (dealId: string) => void;
  onReopen: (dealId: string) => void;
}

const DealsListTableContext = createContext<DealsListTableContextValue | null>(null);

export function DealsListTableProvider({
  value,
  children,
}: {
  value: DealsListTableContextValue;
  children: React.ReactNode;
}) {
  return (
    <DealsListTableContext.Provider value={value}>{children}</DealsListTableContext.Provider>
  );
}

function useDealsListTableContext(): DealsListTableContextValue {
  const ctx = useContext(DealsListTableContext);
  if (!ctx) {
    throw new Error('DealsListTable precisa estar dentro de <DealsListTableProvider>');
  }
  return ctx;
}

/** Cabeçalho ordenável reaproveitado por várias colunas. */
function SortableHead({
  label,
  field,
}: {
  label: string;
  field: DealSortField;
}) {
  const { sortField, sortDirection, onSort } = useDealsListTableContext();
  return (
    <TableHead
      className="text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

interface DealsListTableProps {
  deals: PaginatedDeal[];
  /** Controla a altura do container (listagem usa h-full; grupos usam auto). */
  containerClassName?: string;
  /** Quando false, a tabela não vira viewport de scroll (usada dentro de grupos). */
  scrollViewport?: boolean;
}

export function DealsListTable({
  deals,
  containerClassName = 'h-full',
  scrollViewport = true,
}: DealsListTableProps) {
  const {
    bulkEditMode,
    selectedDealIds,
    onToggleDealSelection,
    onToggleSelectAll,
    onViewDeal,
    onEditDeal,
    computeRowPerms,
    onMarkAsWon,
    onMarkAsLost,
    onReopen,
  } = useDealsListTableContext();

  return (
    <Table
      scrollViewport={scrollViewport}
      containerClassName={containerClassName}
      className="min-w-[1900px]"
    >
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {/* Checkbox de seleção em massa */}
          {bulkEditMode && (
            <TableHead className="w-10 px-2">
              <Checkbox
                checked={
                  deals.length > 0 && deals.every((d) => selectedDealIds.has(d.id))
                }
                onCheckedChange={() => onToggleSelectAll(deals)}
                aria-label="Selecionar todos"
              />
            </TableHead>
          )}
          {/* Indicador de prioridade - não ordenável */}
          <TableHead className="w-8 px-2" />
          <TableHead className="text-xs font-medium w-24">N. Negócio</TableHead>
          <TableHead className="text-xs font-medium">Negócio</TableHead>
          <TableHead className="text-xs font-medium w-40">Cliente</TableHead>
          <SortableHead label="Valor" field="value" />
          <SortableHead label="Prob." field="probability" />
          <SortableHead label="Idade" field="created_at" />
          <SortableHead label="Status" field="status" />
          <SortableHead label="Fase" field="phase_sort_order" />
          <TableHead className="text-xs font-medium">Workspace</TableHead>
          <TableHead className="text-xs font-medium">Pipeline</TableHead>
          <TableHead className="text-xs font-medium">Origem</TableHead>
          <TableHead className="text-xs font-medium">Cadastro</TableHead>
          <TableHead className="text-xs font-medium">Tags</TableHead>
          <SortableHead label="Previsão" field="expected_close_date" />
          <SortableHead label="Criado em" field="created_at" />
          <SortableHead label="Concluído em" field="actual_close_date" />
          <SortableHead label="Responsável" field="assigned_user_name" />
          <SortableHead label="Vendedor" field="seller_name" />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {deals.map((deal) => (
          <TableRow
            key={deal.id}
            className={cn(
              'cursor-pointer hover:bg-muted/40 group',
              bulkEditMode && selectedDealIds.has(deal.id) && 'bg-primary/5'
            )}
            onClick={() => (bulkEditMode ? onToggleDealSelection(deal.id) : onViewDeal(deal))}
          >
            {/* Checkbox de seleção */}
            {bulkEditMode && (
              <TableCell className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedDealIds.has(deal.id)}
                  onCheckedChange={() => onToggleDealSelection(deal.id)}
                />
              </TableCell>
            )}

            {/* Indicador de Prioridade */}
            <TableCell className="px-2 py-2">
              <DealPriorityIndicator priority={deal.priority} type="bar" />
            </TableCell>

            {/* N. Negócio */}
            <TableCell className="py-2 font-mono text-[13px] text-foreground whitespace-nowrap">
              {formatDealCode(deal.id)}
            </TableCell>

            {/* Título + Empresa */}
            <TableCell className="py-2 max-w-[320px]">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{deal.title}</p>
                {deal.company_name && (
                  <p className="text-xs text-muted-foreground truncate">{deal.company_name}</p>
                )}
              </div>
            </TableCell>

            {/* Cliente */}
            <TableCell className="py-2">
              {deal.contact_name ? (
                <div className="flex items-center gap-2 min-w-0">
                  <SmartAvatar
                    src={deal.contact_avatar ?? undefined}
                    name={deal.contact_name}
                    size="xs"
                  />
                  <span className="text-sm truncate max-w-[120px]">{deal.contact_name}</span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Valor */}
            <TableCell className="py-2">
              <span className="text-sm font-mono font-medium">
                <PriceDisplay amount={deal.value} currency={deal.currency} size="sm" />
              </span>
            </TableCell>

            {/* Probabilidade */}
            <TableCell className="py-2">
              <span
                className={cn(
                  'text-xs font-medium tabular-nums',
                  (deal.probability ?? 0) >= 70
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : (deal.probability ?? 0) >= 40
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400'
                )}
              >
                {deal.probability ?? 0}%
              </span>
            </TableCell>

            {/* Idade */}
            <TableCell className="py-2">
              <DealAgeIndicator createdAt={deal.created_at} />
            </TableCell>

            {/* Status */}
            <TableCell className="py-2">
              <DealStatusBadge status={deal.status} />
            </TableCell>

            {/* Fase com barra de progresso */}
            <TableCell className="py-2">
              {deal.phase_id && deal.pipeline_id ? (
                <PhaseProgressCell deal={deal} pipelineId={deal.pipeline_id} />
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Workspace */}
            <TableCell className="py-2">
              {deal.workspace_name ? (
                <span className="text-xs text-foreground truncate max-w-[100px] block">
                  {deal.workspace_name}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Pipeline */}
            <TableCell className="py-2">
              {deal.pipeline_name ? (
                <span className="text-xs text-foreground truncate max-w-[100px] block">
                  {deal.pipeline_name}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Origem */}
            <TableCell className="py-2">
              {deal.source ? (
                <LeadSourceBadge sourceId={deal.source} size="xs" />
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Cadastro (origem do registro) */}
            <TableCell className="py-2">
              {deal.creation_origin ? (
                <DealOriginBadge origin={deal.creation_origin} className="py-0 leading-none" />
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Tags */}
            <TableCell className="py-2">
              {deal.tags && deal.tags.length > 0 ? (
                <div className="flex items-center gap-0.5 max-w-[140px]">
                  {deal.tags.slice(0, 2).map((tag) => (
                    <DealTagBadge
                      key={tag.tag_id}
                      name={tag.tag_name}
                      color={tag.tag_color}
                      size="sm"
                    />
                  ))}
                  {deal.tags.length > 2 && (
                    <span className="text-[9px] text-muted-foreground ml-0.5 shrink-0">
                      +{deal.tags.length - 2}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Data Prevista */}
            <TableCell className="py-2">
              {deal.expected_close_date ? (
                <span className="text-xs text-muted-foreground">
                  {format(new Date(deal.expected_close_date), 'dd/MM/yy', { locale: ptBR })}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Criado em */}
            <TableCell className="py-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {format(new Date(deal.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}
              </span>
            </TableCell>

            {/* Concluído em */}
            <TableCell className="py-2">
              {deal.actual_close_date ? (
                <span
                  className={cn(
                    'text-xs whitespace-nowrap',
                    deal.status === 'won' && 'text-emerald-600 font-medium',
                    deal.status === 'lost' && 'text-rose-600 font-medium',
                    deal.status !== 'won' && deal.status !== 'lost' && 'text-muted-foreground'
                  )}
                >
                  {format(new Date(deal.actual_close_date), 'dd/MM/yy', { locale: ptBR })}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Responsável */}
            <TableCell className="py-2">
              {deal.assigned_to ? (
                <div className="flex items-center gap-2">
                  <SmartAvatar
                    src={deal.assigned_user_avatar}
                    name={deal.assigned_user_name}
                    size="xs"
                    use3DFallback
                  />
                  <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                    {deal.assigned_user_name}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Vendedor */}
            <TableCell className="py-2">
              {deal.seller_id ? (
                <div className="flex items-center gap-2">
                  <SmartAvatar
                    src={deal.seller_avatar}
                    name={deal.seller_name}
                    size="xs"
                    use3DFallback
                  />
                  <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                    {deal.seller_name}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground/50">-</span>
              )}
            </TableCell>

            {/* Menu de Ações (guards de permissão alinhados ao Kanban/Modal) */}
            <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const { canEditRow, canChangePhaseRow, canReopenRow } = computeRowPerms(deal);
                const isOpen = deal.status === 'open';
                const showStatusBlock = isOpen ? canChangePhaseRow : canReopenRow;
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Filter className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 p-1.5 shadow-lg border-border/50">
                      {/* Ver Detalhes — sempre visível */}
                      <DropdownMenuItem
                        onClick={() => onViewDeal(deal)}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                      >
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Ver Detalhes</span>
                      </DropdownMenuItem>

                      {/* Editar — apenas com permissão de edição */}
                      {canEditRow && (
                        <DropdownMenuItem
                          onClick={() => onEditDeal(deal)}
                          className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Editar</span>
                        </DropdownMenuItem>
                      )}

                      {showStatusBlock && <DropdownMenuSeparator className="my-1.5" />}

                      {isOpen && canChangePhaseRow && (
                        <>
                          <DropdownMenuItem
                            onClick={() => onMarkAsWon(deal.id)}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600 focus:bg-emerald-50 dark:focus:bg-emerald-950/30"
                          >
                            <Trophy className="h-4 w-4" />
                            <span className="text-sm font-medium">Marcar como Ganho</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onMarkAsLost(deal.id)}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1"
                          >
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Marcar como Perdido</span>
                          </DropdownMenuItem>
                        </>
                      )}

                      {!isOpen && canReopenRow && (
                        <DropdownMenuItem
                          onClick={() => onReopen(deal.id)}
                          className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                        >
                          <RotateCcw className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Reabrir Negócio</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
